import PropTypes from 'prop-types';
import { debounce, sum, uniqBy } from 'lodash';
import React, { PureComponent } from 'react';
import ReactDOM from 'react-dom';
import Linkify from 'linkify-react';

import { shareableEntityPropTypes } from '../../hoc/shareableEntity';
import { collectibleEntityPropTypes } from '../../hoc/collectibleEntity';
import { queueOriginComponentPropTypes } from '../../hoc/queueOriginComponent';
import { playlistableEntityPropTypes } from '../../hoc/playlistableEntity';
import {
  List as VirtualizedList,
  AutoSizer,
  WindowScroller,
  CellMeasurerCache,
  CellMeasurer,
} from 'react-virtualized';

import classnames from 'classnames';
import { messages as radioButtonMessages } from './PlayRadioButton';
import { defineMessages, FormattedDate, FormattedMessage, intlShape, injectIntl } from 'react-intl';

import * as Shapes from '../../shapes';

import PlaylistItem from '../playlist/PlaylistItem';
import PlayButton from './PlayButton';

import CollectionButton from './CollectionButton';
import ImageCredit from '../common/ImageCredit';
import isDuplicatePiece from '../../utils/isDuplicatePiece';
import ShareButton from '../common/ShareButton';
import BookletButton from '../common/BookletButton';
import CapacitorHeaderBar from '../capacitor/HeaderBar';
import CapacitorDownloadButton from '../capacitor/DownloadButton';
import CapacitorSkeletonIfLoading from '../capacitor/SkeletonIfLoading';

import styles from './GenericTracklistView.css';
import Image from '../util/Image';

import AddToPlaylistButton from './AddToPlaylistButton';
import ContextMenu from '../util/ContextMenu';
import ContextMenuItem from '../util/ContextMenuItem';
import TrackCount from '../messages/TrackCount';
import FormattedTime from '../util/FormattedTime';
import Feature from '../util/Feature';
import { editableEntityPropTypes } from '../../hoc/editableEntity';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { reorder } from '../../utils/array';
import { ENTITY_TYPE_MIX, ENTITY_TYPE_ALBUM } from '../../constants';
import { PLAYABLE_ITEM_FULL_DIMS } from '../../lib/imgix';
import IconLabel from '../util/IconLabel';
import AddToPlaylistContextMenuItem from './AddToPlaylistContextMenuItem';
import ShareContextMenuItem from './ShareContextMenuItem';
import ContentfulLink from '../util/ContentfulLink';
import BrowseElement from '../browse/BrowseElement';
import Expandable from './Expandable';
import Separated from '../util/Separated';
import Slider from './Slider';
import PlayableItemPreview from './PlayableItemPreview';
import RemoveFromPlaylistButton from './RemoveFromPlaylistButton';
import CollectionContextMenuItem from './CollectionContextMenuItem';
import RemoveFromPlaylistContextMenuItem from './RemoveFromPlaylistContextMenuItem';
import Facts from './Facts';
import FloatingScrollButton from './FloatingScrollButton';

const messages = defineMessages({
  sharePlaylist: {
    id: 'playlist.share',
    defaultMessage: 'Share playlist',
  },
  shareAlbum: {
    id: 'album.share',
    defaultMessage: 'Share album',
  },
});

const EditContextMenuItems = ({ onRename, onDelete, onBatchSelect }) => (
  <React.Fragment>
    {!__CAPACITOR__ && (
      <ContextMenuItem onClick={onBatchSelect}>
        <IconLabel
          name="check"
          size="default"
          labelVisible
          label={
            <FormattedMessage
              id="generic-track-list.editable.context-menu.select-tracks"
              defaultMessage="Select Tracks"
            />
          }
        />
      </ContextMenuItem>
    )}
    <ContextMenuItem onClick={onRename}>
      <IconLabel
        name="rename"
        size="default"
        labelVisible
        label={
          <FormattedMessage
            id="generic-track-list.editable.context-menu.rename"
            defaultMessage="Rename"
          />
        }
      />
    </ContextMenuItem>
    <ContextMenuItem onClick={onDelete}>
      <IconLabel
        name="delete"
        size="default"
        labelVisible
        label={
          <FormattedMessage
            id="generic-track-list.editable.context-menu.delete"
            defaultMessage="Delete"
          />
        }
      />
    </ContextMenuItem>
  </React.Fragment>
);

EditContextMenuItems.propTypes = {
  onRename: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onBatchSelect: PropTypes.func.isRequired,
};

class GenericTrackListView extends PureComponent {
  static propTypes = {
    userIsPatron: PropTypes.bool.isRequired,
    playlist: Shapes.Playlist.isRequired,
    queueItem: PropTypes.object,
    isOnlyRadioAvailable: PropTypes.bool,
    recommendations: PropTypes.array,

    imageAnnotation: PropTypes.object,
    pathname: PropTypes.string.isRequired,
    messages: PropTypes.object.isRequired,

    hideDownloadButton: PropTypes.bool,
    showAllTracksDuration: PropTypes.bool,
    entityType: PropTypes.oneOf(['album', 'playlist', 'personal_playlist', ENTITY_TYPE_MIX])
      .isRequired,

    ...shareableEntityPropTypes,
    ...playlistableEntityPropTypes,
    ...editableEntityPropTypes,
    ...collectibleEntityPropTypes,
    ...queueOriginComponentPropTypes,

    intl: intlShape,
    track: PropTypes.func,

    bookletUrl: PropTypes.string,

    collectionIds: PropTypes.object,
    showModal: PropTypes.func,
    facts: PropTypes.array,
  };

  static defaultProps = {
    isEditable: false,
    showAllTracksDuration: true,
  };

  state = {
    isBatchSelectMode: false,
    selectedTrackIndices: new Set(),
  };

  constructor(props, context) {
    super(props, context);
    this._cache = new CellMeasurerCache({
      fixedWidth: true,
      minHeight: __CAPACITOR__ ? 70 : 52,
      defaultHeight: __CAPACITOR__ ? 70 : 52,
    });
  }

  componentDidMount() {
    window.addEventListener('resize', this.handleResize);
  }

  componentWillReceiveProps(nextProps) {
    if (this.props.playlist.id !== nextProps.playlist.id) {
      // In case one playlist has items with different item than the other
      this._cache.clearAll();
    }
  }

  componentDidUpdate(prevProps) {
    if (!this.props.isEditable) {
      return;
    }
    const oldTracks = prevProps.playlist.tracks.map(t => t.id).join('');
    const newTracks = this.props.playlist.tracks.map(t => t.id).join('');
    if (oldTracks !== newTracks) {
      // Necessary for personal playlists if an expanded item changes position
      this._cache.clearAll();
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
  }

  onDragEnd = result => {
    if (!result.destination) {
      return;
    }

    const trackIds = this.props.trackIds;
    const from = result.source.index;
    const to = result.destination.index;
    const updatedTrackIds = reorder(trackIds, from, to);
    const action = { from, to, trackId: trackIds[from] };
    this.props.onReorder(updatedTrackIds, action);
  };

  onRecommendationItemClick = ({ content }) => {
    this.props.track('Selected Content From Category', {
      id: content.id,
      type: content.type,
      pageName: this.props.entityType,
      slug: this.props.playlist.id,
    });
  };

  onRecommendationItemBeforeTogglePlay = (item, playing) => {
    this.props.trackPlayPausePressed(
      { contextType: this.props.entityType, contextId: this.props.playlist.id },
      playing
    );
  };

  onBatchSelectClick = () => {
    this.setState(prevState => ({
      isBatchSelectMode: !prevState.isBatchSelectMode,
      selectedTrackIndices: new Set(),
    }));
  };

  onSelectTrack = index => {
    this.setState(prevState => {
      const newlySelectedIndices = new Set(prevState.selectedTrackIndices);
      if (newlySelectedIndices.has(index)) {
        newlySelectedIndices.delete(index);
      } else {
        newlySelectedIndices.add(index);
      }
      return { selectedTrackIndices: newlySelectedIndices };
    });
  };

  onSelectAll = () => {
    const { tracks } = this.props.playlist;
    const allIndices = new Set(tracks.map((_, index) => index));
    this.setState({ selectedTrackIndices: allIndices });
  };

  onDeselectAll = () => {
    this.setState({ selectedTrackIndices: new Set() });
  };

  onBatchDelete = () => {
    this.props.onBatchDeleteTracks(this.state.selectedTrackIndices, this.onDeselectAll);
  };

  onBatchAddToPlaylist = () => {
    this.props.onBatchAddToPlaylistClick(this.state.selectedTrackIndices);
  };

  onBatchToggleCollection = () => {
    this.props.onBatchToggleCollection(this.state.selectedTrackIndices);
  };

  getShareButtonText() {
    const { intl, pathname } = this.props;
    const isAlbum = pathname.includes('album');
    if (isAlbum) {
      return intl.formatMessage(messages.shareAlbum);
    }
    return intl.formatMessage(messages.sharePlaylist);
  }

  renderInnerTrackItem = ({ track, index, dragHandleProps, measure }) => {
    const { playlist, isEditable } = this.props;
    const { onDeleteTrack, onAddTrackToPlaylistClick } = this.props;
    const { isQueuedTrack, isPlayingTrack, playFromTrack } = this.props;
    const { isBatchSelectMode, selectedTrackIndices } = this.state;
    const isCurrentTrack = isQueuedTrack(track.id, index);
    const isPlaying = isPlayingTrack(track.id, index);
    const isSelected = selectedTrackIndices.has(index);

    return (
      <PlaylistItem
        indexInPlaylist={index}
        playlist={playlist}
        track={track}
        isCurrentTrack={isCurrentTrack}
        playing={isPlaying}
        onClick={playFromTrack}
        onAddToPlaylistButtonClick={onAddTrackToPlaylistClick}
        isDuplicatePiece={isDuplicatePiece(playlist.tracks, track.piece.id)}
        isInPersonalPlaylist={isEditable}
        removeFromPlaylist={onDeleteTrack}
        dragHandleProps={dragHandleProps}
        entityType={this.props.entityType}
        measure={measure}
        isBatchSelectMode={isBatchSelectMode}
        isSelected={isSelected}
        onSelectTrack={this.onSelectTrack}
      />
    );
  };

  renderTrackItem = ({ index, parent, style }) => {
    const track = this.props.playlist.tracks[index];
    const key = `${track.id}_${index}`;
    return (
      <CellMeasurer cache={this._cache} columnIndex={0} key={key} rowIndex={index} parent={parent}>
        {({ measure }) => (
          <div role="listitem" style={style} className={styles.listItem}>
            {this.renderInnerTrackItem({ track, index, measure })}
          </div>
        )}
      </CellMeasurer>
    );
  };

  renderDraggableTrackItem = ({ index, parent, style }) => {
    const track = this.props.playlist.tracks[index];
    const key = `${track.id}_${index}`;
    return (
      <Draggable key={key} draggableId={key} index={index}>
        {(provided, snapshot) => (
          <CellMeasurer
            cache={this._cache}
            columnIndex={0}
            key={key}
            rowIndex={index}
            parent={parent}
          >
            {({ measure }) => (
              <div
                role="listitem"
                ref={provided.innerRef}
                {...provided.draggableProps}
                className={classnames(styles.draggableItem, styles.listItem, {
                  [styles.isDragging]: snapshot.isDragging,
                })}
                style={{
                  ...style,
                  ...provided.draggableProps.style,
                }}
              >
                {this.renderInnerTrackItem({
                  track,
                  index,
                  dragHandleProps: provided.dragHandleProps,
                  measure,
                })}
              </div>
            )}
          </CellMeasurer>
        )}
      </Draggable>
    );
  };

  renderInnerList = (renderFunction, ref, width) => {
    // We need to virtualize the list rendering, since personal playlists can have
    // hundreds of tracks which renders them unusable in capacitor mode.
    // Virtualizing the lists is complicated by some circumstances:

    // 1. The list items have a dynamic height. Solved by using <CellMeasurer>.
    //    Note that we need to call the .measure() method manually when an items is
    //    expanded/collapsed so the heights are correctly updated.
    // 2. The scrolling element is the window element in capacitor, but the
    //    .content element in other modes. Solved by using the <WindowScroller>
    //    component with a dynamic scrollingElement.
    // 3. Items must be draggable in personal playlist mode. react-beatiful-dnd
    //    plays well with the react-virtualized library in mode="virtual". See this
    //    example: https://github.com/atlassian/react-beautiful-dnd/blob/master/stories/src/virtual/react-virtualized/window-list.jsx
    //    However, there is one known issue that was introduced in the version
    //    12.0.0-beta.11: https://github.com/atlassian/react-beautiful-dnd/issues/2111
    //    which forces us to pin the react-beatiful-dnd dependency to
    //    12.0.0-beta.10 for the time being.
    const tracks = this.props.playlist.tracks;
    const scrollingElement =
      typeof window === 'undefined' || __CAPACITOR__
        ? undefined
        : document.querySelector('.content');
    return (
      // Key needs to be passed as a workaround for a known issue https://github.com/bvaughn/react-virtualized/issues/1256
      <WindowScroller scrollElement={scrollingElement} key={scrollingElement}>
        {({ height, isScrolling, registerChild, onChildScroll, scrollTop }) => (
          <div
            ref={listRef => {
              registerChild(listRef);
              this.listRef = listRef;
            }}
            data-test="list.container"
            data-test-length={tracks.length}
          >
            <VirtualizedList
              // VirtualizedList is memoized and does a shallow prop
              // comparison, this forces it to re-render after resorting
              // the tracks
              sortKey={this.props.tracks.map(track => track.id).join('')}
              rowCount={tracks.length}
              autoHeight
              height={height}
              isScrolling={isScrolling}
              onScroll={onChildScroll}
              scrollTop={scrollTop}
              overscanRowCount={10}
              rowHeight={this._cache.rowHeight}
              width={width}
              ref={ref}
              rowRenderer={renderFunction}
              style={{ overflow: __CAPACITOR__ ? undefined : 'visible' }}
              containerRole="list"
              role="none"
            />
          </div>
        )}
      </WindowScroller>
    );
  };

  renderList = () => {
    return (
      <AutoSizer disableHeight>
        {({ width }) => this.renderInnerList(this.renderTrackItem, undefined, width)}
      </AutoSizer>
    );
  };

  renderDraggableList = () => {
    return (
      <AutoSizer disableHeight>
        {({ width }) => (
          <DragDropContext onDragEnd={this.onDragEnd}>
            <Droppable
              droppableId="droppable-pp"
              mode="virtual"
              getContainerForClone={() => document.getElementById('app-content')}
              renderClone={(provided, snapshot, rubric) => (
                <div
                  role="listitem"
                  ref={provided.innerRef}
                  {...provided.draggableProps}
                  className={classnames(styles.draggableItem, styles.listItem, {
                    [styles.isDragging]: snapshot.isDragging,
                  })}
                >
                  {this.renderInnerTrackItem({
                    track: this.props.playlist.tracks[rubric.source.index],
                    index: rubric.source.index,
                    dragHandleProps: provided.dragHandleProps,
                  })}
                </div>
              )}
            >
              {({ innerRef }) =>
                this.renderInnerList(
                  this.renderDraggableTrackItem,
                  () => {
                    // react-virtualized has no way to get the VirtualizedList DOM node
                    // We need to get to it via the parent ref
                    if (this.listRef && this.listRef.children.length > 0) {
                      innerRef(this.listRef.children[0]);
                    }
                  },
                  width
                )
              }
            </Droppable>
          </DragDropContext>
        )}
      </AutoSizer>
    );
  };

  renderTrackList = () => {
    const { isEditable } = this.props;
    return isEditable ? this.renderDraggableList() : this.renderList();
  };

  renderPlaylistHeader() {
    const { intl } = this.props;
    const { playlist, isPlaying, isCollectible, isInCollection, bookletUrl, entityType } =
      this.props;
    const { description, longDescription, title, imageUrl, tracks } = playlist;
    const { togglePlayAll, toggleIsInCollection } = this.props;
    const { showShareModal, onAddToPlaylistClick } = this.props;
    const { hideDownloadButton } = this.props;
    const { isEditable, onRename, onDelete } = this.props;
    const { showAllTracksDuration } = this.props;
    const playlistDuration = sum(tracks.map(track => track.duration));

    const pausedTitle = this.props.isOnlyRadioAvailable
      ? intl.formatMessage(radioButtonMessages.playAllButtonPausedText)
      : intl.formatMessage(this.props.messages.playAllButtonPausedText);
    const playingTitle = this.props.isOnlyRadioAvailable
      ? intl.formatMessage(radioButtonMessages.playAllButtonPlayingText)
      : intl.formatMessage(this.props.messages.playAllButtonPlayingText);

    return (
      <React.Fragment>
        {__CAPACITOR__ && (
          <CapacitorHeaderBar
            title={this.props.playlist.title}
            onlyShowTitleOnScroll
            contentOverflow
          />
        )}
        <div className={styles.header} data-test="generic-track-list.header">
          <figure className={styles.figure}>
            <Image
              src={imageUrl}
              className={styles.headerImage}
              width={PLAYABLE_ITEM_FULL_DIMS.w}
              height={PLAYABLE_ITEM_FULL_DIMS.h}
              alt=""
            />
            {bookletUrl && (
              <BookletButton
                bookletUrl={bookletUrl}
                trackOpenBooklet={this.trackOpenBooklet}
                className={styles.bookletBtn}
                imageUrl={imageUrl}
                entityType={entityType}
              />
            )}
          </figure>
          <div className={styles.headerInfo}>
            <h1 className={styles.headerTitle}>{title}</h1>
            <div className={styles.headerBtns}>
              <div className={styles.headerBtnsLeft}>
                {!this.state.isBatchSelectMode && (
                  <PlayButton
                    pausedTitle={pausedTitle}
                    playingTitle={playingTitle}
                    onClick={togglePlayAll}
                    playing={isPlaying}
                    moduleVariant="header"
                    labelVisible
                    size="small"
                    data-test="generic-track-list-view.play-btn"
                    isRadio={this.props.isOnlyRadioAvailable}
                  />
                )}
                {!this.state.isBatchSelectMode && bookletUrl && (
                  <BookletButton
                    bookletUrl={bookletUrl}
                    trackOpenBooklet={this.trackOpenBooklet}
                    hideText={false}
                    className={styles.bookletBtn}
                    imageUrl={imageUrl}
                    entityType={entityType}
                  />
                )}
              </div>
              <div className={styles.headerBtnsRight}>
                {!this.state.isBatchSelectMode && isCollectible && (
                  <CollectionButton active={isInCollection} onClick={toggleIsInCollection} />
                )}
                {!this.state.isBatchSelectMode && __CAPACITOR__ && !hideDownloadButton && (
                  <CapacitorDownloadButton
                    entityType={this.props.entityType}
                    entityIdOrSlug={this.props.playlist.id}
                  />
                )}
                {!this.state.isBatchSelectMode && (
                  <Feature id="user_playlists">
                    {allowed => {
                      const showAddToPlaylistButton = onAddToPlaylistClick && tracks.length > 0;
                      const showShareButton = showShareModal;
                      const showEditableButtons = allowed && isEditable;
                      const shareButtonText = this.getShareButtonText();

                      return !__CAPACITOR__ ? (
                        <React.Fragment>
                          {showAddToPlaylistButton && (
                            <AddToPlaylistButton onClick={onAddToPlaylistClick} />
                          )}
                          {showShareButton && (
                            <ShareButton onClick={showShareModal} text={shareButtonText} />
                          )}
                          {showEditableButtons && (
                            <ContextMenu data-test="generic-track-list.context-menu">
                              <EditContextMenuItems
                                onRename={onRename}
                                onDelete={onDelete}
                                onBatchSelect={this.onBatchSelectClick}
                              />
                            </ContextMenu>
                          )}
                        </React.Fragment>
                      ) : (
                        (showEditableButtons || showAddToPlaylistButton || showShareButton) && (
                          <ContextMenu data-test="generic-track-list.context-menu">
                            {showAddToPlaylistButton && (
                              <AddToPlaylistContextMenuItem onClick={onAddToPlaylistClick} />
                            )}
                            {showShareButton && (
                              <ShareContextMenuItem
                                onClick={showShareModal}
                                text={shareButtonText}
                              />
                            )}
                            {showEditableButtons && (
                              <EditContextMenuItems
                                onRename={onRename}
                                onDelete={onDelete}
                                onBatchSelect={this.onBatchSelectClick}
                              />
                            )}
                          </ContextMenu>
                        )
                      );
                    }}
                  </Feature>
                )}
              </div>
            </div>
            {!this.state.isBatchSelectMode && showAllTracksDuration && (
              <Separated>
                {playlist.publishDate && (
                  <FormattedDate
                    value={new Date(playlist.publishDate)}
                    day="2-digit"
                    month="2-digit"
                    year="numeric"
                  />
                )}
                <TrackCount trackCount={playlist.tracks.length} />
                {playlistDuration && <FormattedTime>{playlistDuration}</FormattedTime>}
              </Separated>
            )}
            {description && (
              <div className={styles.description}>
                <Expandable
                  key={`${this.props.playlist.id}-description`}
                  onClick={() =>
                    this.props.track('Viewed Playlist Long Description', {
                      playlistID: this.props.playlist.id,
                    })
                  }
                >
                  <Linkify as="p" options={{ render: this.renderLink }}>
                    {description}
                  </Linkify>
                  {longDescription && this.renderLongDescription()}
                </Expandable>
              </div>
            )}
          </div>
        </div>
      </React.Fragment>
    );
  }

  renderLink = ({ attributes, content }) => {
    const { href, ...props } = attributes;
    return (
      <ContentfulLink uri={href} fromId={this.props.playlist.id} {...props}>
        {content}
      </ContentfulLink>
    );
  };

  renderLongDescriptionParts = longDescriptionParts => {
    return longDescriptionParts.map((part, i) =>
      part ? (
        <Linkify key={i} as="p" options={{ render: this.renderLink }}>
          {part}
        </Linkify>
      ) : null
    );
  };

  renderLongDescription = () => {
    const { longDescription } = this.props.playlist;
    if (!longDescription) {
      return null;
    }
    const longDescriptionParts = longDescription.split(/\r\n|\r|\n/g);
    return this.renderLongDescriptionParts(longDescriptionParts);
  };

  renderCredits() {
    const { imageAnnotation, playlist } = this.props;
    if (!imageAnnotation && !playlist.copyright) {
      return null;
    }

    return (
      <div className={styles.credits}>
        {imageAnnotation && <ImageCredit annotation={imageAnnotation} />}
        {playlist.copyright && <div>©℗ {playlist.copyright}</div>}
      </div>
    );
  }

  renderRecommendationItem = item => {
    return (
      <PlayableItemPreview
        item={item}
        key={item.content.id}
        onClick={this.onRecommendationItemClick}
        beforeTogglePlay={this.onRecommendationItemBeforeTogglePlay}
      />
    );
  };

  renderRecommendations() {
    const { recommendations } = this.props;
    if (!recommendations || recommendations.length === 0) {
      return null;
    }
    return (
      <div className={styles.recommendations} id="recommendations">
        <Slider
          title={
            <FormattedMessage
              id="generic-track-list.recommendations.title"
              defaultMessage="You might also enjoy..."
            />
          }
          items={recommendations.map(this.renderRecommendationItem)}
        />
      </div>
    );
  }

  renderBatchSelectionBar() {
    const { isBatchSelectMode, selectedTrackIndices } = this.state;
    const { playlist } = this.props;

    if (!isBatchSelectMode) {
      return null;
    }

    const selectedCount = selectedTrackIndices.size;
    const disableButtons = selectedCount === 0;
    const totalCount = playlist.tracks.length;
    const allSelected = selectedCount === totalCount && totalCount > 0;
    const allInCollection = this.props.areAllSelectedTracksInCollection(selectedTrackIndices);

    const batchSelectionBarContent = (
      <div className={styles.batchSelectionBar} data-test="batch-selection-bar">
        <button onClick={this.onBatchSelectClick} className={styles.batchSelectionBarDone}>
          <FormattedMessage id="generic-track-list.batch-edit.done" defaultMessage="Done" />
        </button>
        <div className={styles.batchSelectionBarCenter}>
          <span className={styles.batchSelectionBarCount}>
            <FormattedMessage
              id="generic-track-list.batch-edit.selected-count"
              defaultMessage="{count} selected"
              values={{ count: selectedCount }}
            />
          </span>
          <span className={styles.batchSelectionBarDot}>·</span>
          <button
            onClick={allSelected ? this.onDeselectAll : this.onSelectAll}
            className={styles.batchSelectionBarSelectAll}
          >
            {allSelected ? (
              <FormattedMessage
                id="generic-track-list.batch-edit.deselect-all"
                defaultMessage="Deselect all"
              />
            ) : (
              <FormattedMessage
                id="generic-track-list.batch-edit.select-all"
                defaultMessage="Select all"
              />
            )}
          </button>
        </div>
        <div className={styles.batchSelectionBarActions}>
          <CollectionButton
            onClick={this.onBatchToggleCollection}
            disabled={disableButtons}
            active={allInCollection}
          />
          <AddToPlaylistButton onClick={this.onBatchAddToPlaylist} disabled={disableButtons} />
          <RemoveFromPlaylistButton onClick={this.onBatchDelete} disabled={disableButtons} />
        </div>
        <div className={styles.batchSelectionBarContextMenu}>
          <ContextMenu>
            <CollectionContextMenuItem
              onClick={this.onBatchToggleCollection}
              active={allInCollection}
              disabled={disableButtons}
            />
            <AddToPlaylistContextMenuItem
              onClick={this.onBatchAddToPlaylist}
              disabled={disableButtons}
            />
            <RemoveFromPlaylistContextMenuItem
              onClick={this.onBatchDelete}
              disabled={disableButtons}
            />
          </ContextMenu>
        </div>
      </div>
    );

    // Use portal to render at document body level to avoid stacking context issues on mobile
    if (typeof document !== 'undefined') {
      return ReactDOM.createPortal(batchSelectionBarContent, document.body);
    }

    return batchSelectionBarContent;
  }

  renderPlaylistBody() {
    const { isBatchSelectMode } = this.state;
    return (
      <div
        className={classnames(styles.body)}
        data-test="generic-track-list.body"
        data-test-length={this.props.playlist.tracks.length}
      >
        {this.renderTrackList()}
        {!isBatchSelectMode && this.renderParticipants()}
        {!isBatchSelectMode && this.renderFacts()}
        {!isBatchSelectMode && this.renderRecommendations()}
        {!isBatchSelectMode && this.renderCredits()}
        {this.renderBatchSelectionBar()}
        {!isBatchSelectMode && this.renderFloatingScrollButton()}
      </div>
    );
  }

  renderFloatingScrollButton() {
    const { facts, recommendations, playlist } = this.props;
    const hasFacts = facts && facts.length > 0;
    const hasRecommendations = recommendations && recommendations.length > 0;
    const hasParticipants = playlist.participants && playlist.participants.length > 0;
    if (!hasFacts && !hasRecommendations && !hasParticipants) {
      return null;
    }

    let target;
    if (hasParticipants) {
      target = 'participants';
    } else if (hasFacts) {
      target = 'facts';
    } else if (hasRecommendations) {
      target = 'recommendations';
    }
    return <FloatingScrollButton target={target} />;
  }

  renderFacts() {
    const { facts } = this.props;
    if (!facts || facts.length === 0) {
      return null;
    }

    return (
      <Facts
        facts={facts}
        bookletUrl={this.props.bookletUrl}
        entityType={this.props.entityType}
        entityId={this.props.playlist.id}
        screen={this.props.entityType === ENTITY_TYPE_ALBUM ? 'Album' : 'Playlist'}
        imageUrl={this.props.playlist.imageUrl}
      />
    );
  }

  renderParticipants() {
    const { participants } = this.props.playlist;
    if (!participants) {
      return null;
    }
    const uniqueParticipants = uniqBy(participants, 'id');
    const featuredParticipants = uniqueParticipants.slice(0, 6);
    const remainingParticipants = uniqueParticipants.slice(6);

    return (
      <div id="participants" className={styles.participants}>
        <h2 className="fz--gamma">
          <FormattedMessage id="generic-track-list.participants.title" defaultMessage="Featuring" />
        </h2>
        <ul className={styles.participantsList}>
          {featuredParticipants.map(participant => (
            <li key={participant.id}>
              <BrowseElement
                id={participant.id}
                name={participant.name}
                href={`/profiles/${participant.id}`}
                showCollectionButton
              />
            </li>
          ))}
        </ul>
        {remainingParticipants.length > 0 && (
          <Expandable key={`${this.props.playlist.id}-participants`}>
            <ul className={styles.participantsList}>
              {remainingParticipants.map(participant => (
                <li key={participant.id}>
                  <BrowseElement
                    id={participant.id}
                    name={participant.name}
                    href={`/profiles/${participant.id}`}
                    showCollectionButton
                  />
                </li>
              ))}
            </ul>
          </Expandable>
        )}
      </div>
    );
  }

  render() {
    const { pathname } = this.props;
    const stylesView = classnames(styles.view);

    const trackListType = ['album', 'playlist'].find(a => pathname.includes(a));

    return (
      <div className={stylesView} data-test={trackListType ? trackListType + '-page' : null}>
        <CapacitorSkeletonIfLoading withContainer>
          {this.renderPlaylistHeader()}
          {this.renderPlaylistBody()}
        </CapacitorSkeletonIfLoading>
      </div>
    );
  }

  trackOpenBooklet = () => {
    this.props.track('Opened Booklet', { albumID: this.props.playlist.id });
  };

  handleResize = debounce(() => {
    this._cache.clearAll();
  }, 500);
}

export default injectIntl(GenericTrackListView);

export { GenericTrackListView as PureGenericTrackListView };
